import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Copy, Loader2, Mail, MessageCircle, Package, RefreshCw, Search, Send, Store, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { inventoryAPI, relationshipAPI, shopInviteAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductTicker from '@/components/ProductTicker';
import { useToast } from '@/hooks/use-toast';

const EMPTY = { name: '', phone: '', email: '' };

function detailOf(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return { message: detail };
  if (detail?.message) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return { message: detail[0].msg.replace(/^Value error, /, '') };
  return {};
}

/** "08031234567" or "8031234567" -> "+2348031234567"; "+..." kept as typed. */
function fullPhone(raw) {
  const digits = raw.replace(/[^\d+]/g, '');
  if (!digits) return '';
  if (digits.startsWith('+')) return digits;
  return `+234${digits.replace(/^0+/, '')}`;
}

/**
 * Asks which of my products a shop gets, before an invite is sent.
 * `ask(shopName)` opens it and resolves with the ticked ids, or null if
 * cancelled.
 */
function useProductChoice() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [state, setState] = useState(null); // { name, resolve }
  const [selected, setSelected] = useState(new Set());

  const ask = useCallback(async (name) => {
    if (items === null) {
      try {
        const res = await inventoryAPI.get(user.id);
        setItems((res.data || []).map((i) => ({ product_id: i.product_id, name: i.product_name,
                                                 nafdac_number: i.nafdac_number, unit: i.unit })));
      } catch { setItems([]); }
    }
    setSelected(new Set());
    return new Promise((resolve) => setState({ name, resolve }));
  }, [items, user.id]);

  const close = (value) => {
    state?.resolve(value);
    setState(null);
  };

  const dialog = (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) close(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('shopProducts.chooseTitle', { name: state?.name || '' })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">{t('shopProducts.chooseText')}</p>
        {items === null ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" /> : (
          <ProductTicker items={items} selected={selected} onChange={setSelected} />
        )}
        <Button className="w-full h-12 mt-2 bg-farm-primary hover:bg-farm-primary-dark font-bold"
                onClick={() => close([...selected])} data-testid="choose-products-confirm">
          {t('shopProducts.sendWith', { n: selected.size })}
        </Button>
      </DialogContent>
    </Dialog>
  );
  return { ask, dialog };
}

/**
 * A distributor adds shops: find one already on African Farm and invite
 * it, or invite a shop that isn't on the app by SMS and/or email. Either
 * way the distributor ticks which of their products the shop gets; a new
 * shop gets them when it registers with that phone number or email. The
 * products can be changed later (My shops -> Choose products).
 */
export default function AddShopkeeperPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [shops, setShops] = useState([]);
  const choice = useProductChoice();

  const loadInvites = useCallback(async () => {
    try {
      const [inv, mine] = await Promise.all([shopInviteAPI.list(), shopInviteAPI.myShops()]);
      setInvites(inv.data.invites);
      setShops(mine.data.shops);
    } catch { /* the lists are a convenience */ }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  return (
    <div className="p-4 md:p-6 animate-fadeIn space-y-5 pb-8">
      <div>
        <button type="button" onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-gray-700 font-semibold h-10 -ml-1 mb-2">
          <ArrowLeft className="w-5 h-5" /> {t('wizard.back')}
        </button>
        <h1 className="font-heading text-2xl font-bold">{t('addShop.title')}</h1>
        <p className="text-muted-foreground">{t('addShop.subtitle')}</p>
      </div>
      <FindShop askProducts={choice.ask} onSent={loadInvites} />
      <InviteNewShop askProducts={choice.ask} onSent={loadInvites} />
      <MyShops shops={shops} />
      <InviteList invites={invites} onChange={loadInvites} />
      {choice.dialog}
    </div>
  );
}

function MyShops({ shops }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!shops.length) return null;
  return (
    <section className="rounded-2xl border bg-white p-4">
      <h2 className="font-semibold text-lg mb-1">{t('shopProducts.myShops')}</h2>
      <p className="text-sm text-gray-600 mb-2">{t('shopProducts.myShopsText')}</p>
      <ul className="divide-y" data-testid="my-shops">
        {shops.map((shop) => (
          <li key={shop.id} className="py-3 flex items-center gap-3">
            <Store className="w-6 h-6 text-farm-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold break-words">{shop.name}</p>
              <p className="text-sm text-gray-600">{t('shopProducts.count', { n: shop.products })}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate(`/shopkeepers/${shop.id}/products`)}
                    data-testid={`my-shop-products-${shop.id}`}>
              <Package className="w-4 h-4 mr-1" /> {t('shopProducts.open')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FindShop({ askProducts, onSent }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [shops, setShops] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setShops(null); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const res = await shopInviteAPI.search(term);
        setShops(res.data.shops);
      } catch { setShops([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const invite = async (shop) => {
    const productIds = await askProducts(shop.business_name || shop.name);
    if (productIds === null) return;
    setBusyId(shop.id);
    try {
      await relationshipAPI.invite({ sender_id: user.id, recipient_id: shop.id,
                                     relationship_type: 'distributor_shopkeeper', product_ids: productIds });
      setShops((list) => list.map((s) => (s.id === shop.id ? { ...s, status: 'pending' } : s)));
      toast({ title: t('addShop.find.sent', { name: shop.business_name || shop.name }) });
      onSent();
    } catch (err) {
      toast({ title: detailOf(err).message || t('addShop.failed'), variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rounded-2xl border bg-white p-4">
      <h2 className="font-semibold text-lg flex items-center gap-2"><Search className="w-5 h-5 text-farm-primary" />{t('addShop.find.title')}</h2>
      <p className="text-sm text-gray-600 mb-3">{t('addShop.find.text')}</p>
      <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-12 text-base"
             placeholder={t('addShop.find.placeholder')} data-testid="shop-search" />
      {shops && shops.length === 0 && <p className="text-sm text-gray-600 mt-3">{t('addShop.find.none')}</p>}
      {shops && shops.length > 0 && (
        <ul className="divide-y mt-2" data-testid="shop-results">
          {shops.map((shop) => (
            <li key={shop.id} className="flex items-center gap-3 py-3">
              <Store className="w-6 h-6 text-farm-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold break-words">{shop.business_name || shop.name}</p>
                <p className="text-sm text-gray-600 break-words">{[shop.business_name && shop.name, shop.location].filter(Boolean).join(' · ')}</p>
              </div>
              {shop.status === 'accepted' && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/shopkeepers/${shop.id}/products`)}
                        data-testid={`shop-result-products-${shop.id}`}>
                  <Package className="w-4 h-4 mr-1" /> {t('shopProducts.open')}
                </Button>
              )}
              {shop.status === 'pending' && <span className="text-sm font-semibold text-amber-700">{t('addShop.find.invited')}</span>}
              {!shop.status && (
                <Button size="sm" className="bg-farm-primary hover:bg-farm-primary-dark" disabled={busyId === shop.id}
                        onClick={() => invite(shop)} data-testid={`shop-invite-${shop.id}`}>
                  {busyId === shop.id ? <Loader2 className="w-4 h-4 animate-spin" /> : t('addShop.find.invite')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InviteNewShop({ askProducts, onSent }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const phone = fullPhone(form.phone);
    if (!form.name.trim()) {
      toast({ title: t('addShop.invite.needName'), variant: 'destructive' });
      return;
    }
    if (!phone && !form.email.trim()) {
      toast({ title: t('addShop.invite.needContact'), variant: 'destructive' });
      return;
    }
    const productIds = await askProducts(form.name.trim());
    if (productIds === null) return;
    setBusy(true);
    setExisting(null);
    try {
      const res = await shopInviteAPI.invite({ name: form.name.trim(), phone: phone || null,
                                               email: form.email.trim() || null, product_ids: productIds });
      setResult(res.data);
      setForm(EMPTY);
      onSent();
    } catch (err) {
      const detail = detailOf(err);
      if (detail.code === 'already_on_app') setExisting(detail.shop);
      else toast({ title: detail.message || t('addShop.failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const inviteExisting = async () => {
    const productIds = await askProducts(existing.name);
    if (productIds === null) return;
    try {
      await relationshipAPI.invite({ sender_id: user.id, recipient_id: existing.id,
                                     relationship_type: 'distributor_shopkeeper', product_ids: productIds });
      toast({ title: t('addShop.find.sent', { name: existing.name }) });
      setExisting(null);
      setForm(EMPTY);
    } catch (err) {
      toast({ title: detailOf(err).message || t('addShop.failed'), variant: 'destructive' });
    }
  };

  return (
    <section className="rounded-2xl border bg-white p-4">
      <h2 className="font-semibold text-lg flex items-center gap-2"><Send className="w-5 h-5 text-farm-primary" />{t('addShop.invite.title')}</h2>
      <p className="text-sm text-gray-600 mb-3">{t('addShop.invite.text')}</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="shop-name">{t('addShop.invite.name')}</Label>
          <Input id="shop-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="h-12 text-base" maxLength={120} data-testid="new-shop-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shop-phone">{t('addShop.invite.phone')}</Label>
          <Input id="shop-phone" type="tel" inputMode="tel" value={form.phone} placeholder="0803 123 4567"
                 onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 text-base"
                 data-testid="new-shop-phone" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shop-email">{t('addShop.invite.email')}</Label>
          <Input id="shop-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="h-12 text-base" data-testid="new-shop-email" />
        </div>
        <p className="text-xs text-gray-500">{t('addShop.invite.either')}</p>
        <Button type="submit" disabled={busy} className="w-full h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark"
                data-testid="new-shop-send">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t('addShop.invite.send')}
        </Button>
      </form>

      {existing && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="new-shop-existing">
          <p className="text-sm font-medium mb-2">{t('addShop.invite.alreadyOnApp', { name: existing.name })}</p>
          <Button size="sm" className="bg-farm-primary" onClick={inviteExisting}>{t('addShop.find.invite')}</Button>
        </div>
      )}
      {result && <InviteResult result={result} />}
    </section>
  );
}

function InviteResult({ result }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { invite, sent, link } = result;
  const channel = (key, Icon) => sent[key] && (
    <p className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4" />
      {t(`addShop.result.${key}.${sent[key]}`)}
    </p>
  );
  const shareText = t('addShop.result.shareText', { name: invite.name, link,
    contact: invite.phone || invite.email });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: t('addShop.result.copied') });
    } catch { /* clipboard blocked */ }
  };
  return (
    <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-3 space-y-2" data-testid="new-shop-result">
      <p className="font-semibold flex items-center gap-2"><Check className="w-5 h-5 text-green-700" />{t('addShop.result.title', { name: invite.name })}</p>
      {channel('sms', MessageCircle)}
      {channel('email', Mail)}
      <p className="text-sm text-gray-700">{t('addShop.result.shareHint')}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="bg-white" onClick={copy} data-testid="new-shop-copy">
          <Copy className="w-4 h-4 mr-1" /> {t('addShop.result.copy')}
        </Button>
        <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center rounded-md px-3 text-sm font-medium bg-[#25D366] text-white"
           data-testid="new-shop-whatsapp">
          <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
        </a>
      </div>
    </div>
  );
}

function InviteList({ invites, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  if (!invites.length) return null;

  const resend = async (inv) => {
    try {
      const res = await shopInviteAPI.resend(inv.id);
      const sentAny = Object.values(res.data.sent).includes('sent');
      toast({ title: sentAny ? t('addShop.list.resent') : t('addShop.list.resentLinkOnly') });
      onChange();
    } catch {
      toast({ title: t('addShop.failed'), variant: 'destructive' });
    }
  };
  const cancel = async (inv) => {
    if (!window.confirm(t('addShop.list.confirmCancel', { name: inv.name }))) return;
    await shopInviteAPI.cancel(inv.id).catch(() => {});
    onChange();
  };

  return (
    <section className="rounded-2xl border bg-white p-4">
      <h2 className="font-semibold text-lg mb-2">{t('addShop.list.title')}</h2>
      <ul className="divide-y" data-testid="shop-invite-list">
        {invites.map((inv) => (
          <li key={inv.id} className="py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold break-words">{inv.name}</p>
              <p className="text-sm text-gray-600 break-words">{[inv.phone, inv.email].filter(Boolean).join(' · ')}</p>
              {inv.status !== 'joined' && (
                <p className="text-xs text-gray-500">{t('shopProducts.chosen', { n: inv.products_chosen })}</p>
              )}
            </div>
            {inv.status === 'joined' ? (
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-green-700">{t('addShop.list.joined')}</span>
                {inv.shopkeeper_id && (
                  <Button size="icon" variant="ghost" aria-label={t('shopProducts.open')}
                          onClick={() => navigate(`/shopkeepers/${inv.shopkeeper_id}/products`)}>
                    <Package className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-amber-700 mr-1">{t('addShop.list.waiting')}</span>
                <Button size="icon" variant="ghost" onClick={() => resend(inv)} aria-label={t('addShop.list.resend')}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => cancel(inv)} aria-label={t('addShop.list.cancel')}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
